/**
 * M7 Zod schemas (plan S0): the Career story block (saves), the worker command mirror (tests and dev builds) and
 * every `src/data/story/**` file. Strict objects throughout: an unknown key is an error, so a typo in content data
 * is named instead of ignored.
 *
 * One section per data file. The task that owns a data file's content may refine its own section only (additive:
 * new keys get a `.default()` so the other files and older saves still parse); the Career block and the command
 * mirror are append-only. Owners: logging S1, evidence S2/S3 (S7 checks the budget), claims S7, case S8, comms S9,
 * clock S10, leverage S5, callbacks S11, missions S6 (runtime) and S12/S13 (content), reactivation S15,
 * finale S21/S25.
 *
 * Every number here is a spec placeholder (spec §15 item 4) for the balance pass; none lives in code.
 */
import { z } from 'zod'
import { COMPONENT_TYPES, CONTRACT_TYPES, SIM_FACTION_IDS } from '../data/m2Schemas'
import { FACTION_IDS, MEASURE_NAMES } from '../strategic/types'
import { goodIdSchema } from '../career/parts'
import {
  BALLOT_STANCES,
  CASE_LINKS,
  CLAIM_STATES,
  CLOCK_ALERTS,
  CONTACT_INTERACTIONS,
  COUNTDOWN_KINDS,
  FINALE_PATHS,
  FINALE_ROWS,
  FR_CLASSES,
  INCOME_SOURCES,
  ITEM_STATES,
  LEAD_POI_KINDS,
  LOG_KINDS,
  LOG_LINKS,
  LOG_SOURCES,
  MISSION_IDS,
  MISSION_STATES,
  OKAFOR_STATES,
  REACTIVATION_STATES,
  SQUAT_CLEARED,
  REGISTRANTS,
  RETALIATION_ROWS,
  REYES_STATES,
  SPEAKERS,
  STATION_ACTIONS,
  STEP_VERBS,
  STORY_DEBT_KINDS,
  STORY_FLAGS,
  STORY_ITEM_IDS,
  STORY_SHIP_ROLES,
  THREADS,
  VOTE_OUTCOMES,
  WIRE_OUTLETS,
  type IncomeMix,
  type StoryCommand,
  type StoryState,
  type TetherTotals,
} from './types'

const id = z.string().min(1)
const nonNegative = z.number().nonnegative()
const count = z.number().int().nonnegative()
const share = z.number().min(0).max(1)
const day = z.number().int().nonnegative()
/** A placeholder multiplier (clock rows, retaliation): positive and bounded so a typo can't run away. */
const mult = z.number().positive().max(10)
const standingDelta = z.number().min(-100).max(100)
const tierNumber = z.number().int().min(0).max(5)
const claimTier = z.number().int().min(2).max(5)
/** Ids in data files: lower-case words joined by `_`, `-`, `.` or `:`. */
const dataId = z.string().regex(/^[a-z0-9][a-z0-9_.:-]*$/, 'lower-case id (a-z, 0-9, _ . : -)')

export const threadSchema = z.enum(THREADS)
export const caseLinkSchema = z.enum(CASE_LINKS)
export const storyFlagSchema = z.enum(STORY_FLAGS)
export const storyItemIdSchema = z.enum(STORY_ITEM_IDS)
export const finalePathSchema = z.enum(FINALE_PATHS)
export const speakerSchema = z.enum(SPEAKERS)
export const wireOutletSchema = z.enum(WIRE_OUTLETS)
export const missionIdSchema = z.enum(MISSION_IDS)
export const factionIdSchema = z.enum(FACTION_IDS)
export const simFactionIdSchema = z.enum(SIM_FACTION_IDS)
const partySchema = z.union([factionIdSchema, z.literal('player')])
const measureSchema = z.enum(MEASURE_NAMES)

// =============================================================================================
// The Career story block (spec §12.1; append-only). Every member has a default, so a partial block from an
// earlier M7 build fills in; the block itself stays optional on the Career (absent = no story progress).
// =============================================================================================

export const storyItemSchema = z.strictObject({
  id: storyItemIdSchema,
  state: z.enum(ITEM_STATES),
  where: id.optional(),
  contraband: z.boolean().optional(),
  /** S4: the death pod holding the player's item (append-only). */
  pod: id.optional(),
})

export const storyMissionSchema = z.strictObject({
  id: missionIdSchema,
  step: id,
  state: z.enum(MISSION_STATES),
  choices: z.record(id, id).optional(),
  /** S6: the fork offered after the current step, awaiting a choice (append-only). */
  awaitingFork: id.optional(),
})

export const storyClaimSchema = z.strictObject({
  tier: claimTier,
  attempts: count,
  lastAttemptDay: day,
  dayAttempts: count,
  readMark: count,
  failOnlineDay: day.optional(),
  assisted: z.boolean(),
})

export const storyCountdownSchema = z.strictObject({
  id,
  kind: z.enum(COUNTDOWN_KINDS),
  startOnlineDay: day,
  dueOnlineDay: day,
  ref: id.optional(),
})

export const storyClockSchema = z.strictObject({
  alerts: z.array(z.enum(CLOCK_ALERTS)).default(() => []),
  idleDays: day.default(0),
  finaleRow: z.enum(FINALE_ROWS).optional(),
  tierDropsFired: z.array(tierNumber).default(() => []),
})

export const bundleEntrySchema = z.strictObject({ kind: z.enum(['log', 'item']), id })

export const storyBundleSchema = z.strictObject({ path: finalePathSchema, entries: z.array(bundleEntrySchema) })

export const storyFinaleSchema = z.strictObject({
  paths: z.array(finalePathSchema).default(() => []),
  from: finalePathSchema.optional(),
  exitOnlineDay: day.optional(),
  floorUntilOnlineDay: day.optional(),
  vote: z.enum(VOTE_OUTCOMES).optional(),
  stipend: z
    .strictObject({ base: nonNegative, share, fixedOnlineDay: day, paid: nonNegative })
    .optional(),
  repeats: z.record(id, z.strictObject({ count, lastOnlineDay: day })).optional(),
  candleDarkOnlineDay: day.optional(),
  ballot: z.partialRecord(measureSchema, z.enum(BALLOT_STANCES)).optional(),
})

export const storyReactivationSchema = z.strictObject({
  poiId: id,
  state: z.enum(REACTIVATION_STATES),
  registrant: z.enum(REGISTRANTS).optional(),
  recipient: factionIdSchema.optional(),
  lastLitDay: day,
  frClass: z.enum(FR_CLASSES).optional(),
  kit: z
    .strictObject({
      components: z.partialRecord(z.enum(COMPONENT_TYPES), count),
      goods: z.record(id, count),
    })
    .optional(),
  rerestart: z.literal(true).optional(),
  marker: z.literal(true).optional(),
  withFamily: z.literal(true).optional(),
  vented: z.literal(true).optional(),
  cleared: z.enum(SQUAT_CLEARED).optional(),
  handoverOnly: z.literal(true).optional(),
})

export const storyStateSchema: z.ZodType<StoryState> = z.strictObject({
  flags: z.partialRecord(storyFlagSchema, z.literal(true)).default(() => ({})),
  items: z.array(storyItemSchema).default(() => []),
  missions: z.array(storyMissionSchema).default(() => []),
  claims: z.array(storyClaimSchema).default(() => []),
  countdowns: z.array(storyCountdownSchema).default(() => []),
  clock: storyClockSchema.default(() => ({ alerts: [], idleDays: 0, tierDropsFired: [] })),
  bundles: z.array(storyBundleSchema).default(() => []),
  finale: storyFinaleSchema.default(() => ({ paths: [] })),
  reactivations: z.array(storyReactivationSchema).default(() => []),
  onlineDays: day.default(0),
  readLogs: z.array(id).default(() => []),
  unreadLogs: z.array(id).default(() => []),
  leads: z.array(id).default(() => []),
  reyesState: z.enum(REYES_STATES).optional(),
  okaforState: z.enum(OKAFOR_STATES).optional(),
  retaliation: z
    .strictObject({ rows: z.array(z.enum(RETALIATION_ROWS)), mult, sinceOnlineDay: day })
    .optional(),
  debts: z
    .array(
      z.strictObject({
        kind: z.enum(STORY_DEBT_KINDS),
        creditor: simFactionIdSchema,
        amount: nonNegative,
        cyclesLeft: count,
        refinanced: z.boolean().optional(),
        obligationDueCycle: count.optional(),
      }),
    )
    .optional(),
  hale: z
    .strictObject({
      offerDay: day,
      chartersLeft: count,
      bountiesWiped: nonNegative,
      feeWaived: nonNegative,
      /** S13 (append-only). */
      charterIds: z.array(id).optional(),
    })
    .optional(),
  meterProofs: z.array(z.strictObject({ opId: id, day })).optional(),
  corroborations: z.array(z.strictObject({ link: caseLinkSchema, onlineDay: day })).optional(),
  /** S12 (append-only): Holdfast contracts handed in. */
  holdfastContracts: count.optional(),
  nudge: z
    .strictObject({ tier: z.number().int().min(1).max(5), sinceOnlineDay: day, rungs: count, lastOnlineDay: day })
    .optional(),
  /** S13 (append-only): the faction day of Hale's call. */
  haleOfferDay: day.optional(),
  /** S37 (append-only): the M1 and M2 lasting traces. */
  traces: z
    .strictObject({
      columnNine: z.strictObject({ kind: z.enum(['memorial', 'stripped']), onlineDay: day }).optional(),
      barge: z.strictObject({ kind: z.enum(['wreck', 'rig']), onlineDay: day }).optional(),
      m1Crew: z.strictObject({ onlineDay: day }).optional(),
      /** S37d (append-only): the barge wreck was looted. */
      bargeLooted: z.strictObject({ onlineDay: day }).optional(),
    })
    .optional(),
})

export const tetherTotalsSchema: z.ZodType<TetherTotals> = z.strictObject({
  tonnes: nonNegative,
  credits: nonNegative,
  sales: count,
})

const incomeBucketSchema = z.strictObject({
  mining: nonNegative,
  combat: nonNegative,
  salvage: nonNegative,
  trade: nonNegative,
  contracts: nonNegative,
})

export const incomeMixSchema: z.ZodType<IncomeMix> = z.strictObject({
  bucketStart: nonNegative,
  buckets: z.array(incomeBucketSchema),
})

/** A fresh, empty story block (what `storyStateSchema.parse({})` gives). */
export function emptyStoryState(): StoryState {
  return storyStateSchema.parse({})
}

// =============================================================================================
// The worker command mirror (append-only; S16 adds the worker side and keeps the two in step).
// =============================================================================================

const untilOnlineDay = day

export const storyCommandSchema: z.ZodType<StoryCommand> = z.union([
  z.strictObject({ type: z.literal('setStoryTier'), tier: tierNumber }),
  z.strictObject({ type: z.literal('setStoryClock'), storyMult: mult }),
  z.strictObject({ type: z.literal('storyTierDrop'), tier: claimTier, drop: z.number().positive().max(100), cause: z.string() }),
  z.strictObject({ type: z.literal('setWorldFlag'), key: id, value: z.union([z.number(), z.string(), z.boolean()]) }),
  z.strictObject({
    type: z.literal('postWire'),
    headline: id,
    severity: z.number().int().min(0).max(3),
    source: wireOutletSchema,
    systemId: id.optional(),
  }),
  z.strictObject({ type: z.literal('proposeCharterVote'), measure: measureSchema, sponsor: partySchema, evidenceWeight: share }),
  z.strictObject({ type: z.literal('seedPressure'), a: partySchema, b: partySchema, amount: z.number() }),
  z.strictObject({ type: z.literal('setGrievance'), a: partySchema, b: partySchema, floor: z.number(), untilOnlineDay }),
  z.strictObject({ type: z.literal('setAggressionFloor'), faction: factionIdSchema, value: z.number().min(0).max(100), untilOnlineDay }),
  z.strictObject({ type: z.literal('setRetaliation'), faction: factionIdSchema, rows: z.array(z.enum(RETALIATION_ROWS)) }),
  z.strictObject({ type: z.literal('setPlayerBallot'), measure: measureSchema, stance: z.enum(BALLOT_STANCES) }),
  z.strictObject({
    type: z.literal('setClaimState'),
    scope: z.enum(['claim', 'commission']),
    id,
    state: z.enum(CLAIM_STATES),
    untilOnlineDay: untilOnlineDay.nullable(),
  }),
  z.strictObject({
    type: z.literal('spawnStoryShip'),
    key: id,
    role: z.enum(STORY_SHIP_ROLES),
    systemId: id,
    missionId: missionIdSchema.optional(),
  }),
  z.strictObject({ type: z.literal('hiddenTransfer'), from: partySchema, to: factionIdSchema, amount: nonNegative }),
  z.strictObject({ type: z.literal('seedBounty'), faction: factionIdSchema, amount: nonNegative, reason: id }),
  z.strictObject({ type: z.literal('wipeBounties'), faction: factionIdSchema, olderThanDay: day, cap: nonNegative }),
  z.strictObject({ type: z.literal('restoreBounties'), faction: factionIdSchema, amount: nonNegative }),
  z.strictObject({ type: z.literal('registerHoldfastClaims'), registered: z.boolean() }),
  z.strictObject({ type: z.literal('setCharterWaiver'), charters: count, waiveMinStanding: z.boolean() }),
  z.strictObject({ type: z.literal('restoreDerelict'), poiId: id, systemId: id, template: id, owner: partySchema }),
  z.strictObject({
    type: z.literal('registerReactivation'),
    poiId: id,
    registrant: z.enum(REGISTRANTS),
    recipient: factionIdSchema.optional(),
    fee: nonNegative,
    marker: z.boolean().optional(),
    withFamily: z.boolean().optional(),
    waitOnlineDays: count.optional(),
    reapply: z.boolean().optional(),
  }),
  z.strictObject({ type: z.literal('settleRegistryChallenge'), poiId: id, fee: nonNegative }),
  z.strictObject({ type: z.literal('supplyStation'), poiId: id, good: id, qty: count }),
  z.strictObject({ type: z.literal('lowerVoteWeight'), measure: measureSchema, drop: z.number().positive().max(1) }),
  z.strictObject({ type: z.literal('storyDev'), op: z.literal('setOnlineDay'), day }),
  z.strictObject({ type: z.literal('storyDev'), op: z.literal('clearStory') }),
])

// =============================================================================================
// logging.json (S1): the income-mix buckets (spec §12.1, §9). The kill and sale ring sizes stay in
// `career-logs.json` (D-340).
// =============================================================================================

export const loggingFileSchema = z.strictObject({
  comment: z.string(),
  /** Game seconds per income bucket (30 min). */
  incomeBucketS: z.number().positive(),
  /** Buckets kept (10 × 30 min ≈ 5 h). */
  incomeBuckets: z.number().int().positive(),
  /** A source over this share of the window's income gets its retaliation row (spec §9 "~25%"). */
  dominantShare: share,
  /** The income source a raw-ware (ore) sale books to (spec §12.1; S1). */
  wareSaleSource: z.enum(INCOME_SOURCES).default('mining'),
  /** The income source a component (salvage, data core) sale books to. */
  componentSaleSource: z.enum(INCOME_SOURCES).default('salvage'),
  /** Contract types whose hand-in books to `combat` (spec §12.1: clearRaiders); every other type books to `contracts`. */
  combatContractTypes: z.array(z.enum(CONTRACT_TYPES)).default(['clearRaiders']),
})
export type LoggingConfig = z.infer<typeof loggingFileSchema>

// =============================================================================================
// evidence/*.json (S2, S3): logs (spec §2.1). `fromTier`, `item`, `outsideBudget`, `repeatable` and `template` are
// the contract's additions for the budget tests (S7), the first-hour hooks (S14) and the boot logs (§16.6).
// =============================================================================================

export const evidenceLogSchema = z.strictObject({
  id: dataId,
  thread: threadSchema,
  link: z.enum(LOG_LINKS),
  kind: z.enum(LOG_KINDS),
  /** Proof weight (spec §2.5: logs 1, mission originals 3, texture 0). */
  weight: z.number().min(0).max(3),
  title: id,
  /** In-fiction date text. */
  date: id,
  source: z.enum(LOG_SOURCES),
  /** At most ~120 words (S2/S3 test it); may be a caption when `table` carries the fact. */
  body: z.string(),
  /** "Form carries the fact" (C9): one table (queue tables, routing slips, receipts). */
  table: z
    .strictObject({ columns: z.array(id).min(1), rows: z.array(z.array(z.string())).min(1) })
    .optional(),
  /** The claim words this log adds to the pool (spec §2.3). */
  words: z.array(id).default(() => []),
  lead: z.strictObject({ systemHint: id, poiKind: z.enum(LEAD_POI_KINDS) }).optional(),
  /** The earliest story tier at which this log can drop (1 = from the start). */
  fromTier: z.number().int().min(1).max(5).default(1),
  /** The story item whose contents this log is (a mission item's log). */
  item: storyItemIdSchema.optional(),
  /** Outside the clue budget (§2.2): the FR station boot logs (§16.6, T33). */
  outsideBudget: z.boolean().default(false),
  /** A repeatable corroboration template (§2.5): one per link. */
  repeatable: z.boolean().default(false),
  /** Generated from a template with a seeded slot (the Reyes-named pilot, §2.4). */
  template: z.boolean().default(false),
})
export type EvidenceLog = z.output<typeof evidenceLogSchema>

export const evidenceFileSchema = z.strictObject({
  comment: z.string(),
  logs: z.array(evidenceLogSchema),
})
export type EvidenceFileRaw = z.input<typeof evidenceFileSchema>

// =============================================================================================
// hooks.json (S14): the first-hour hooks (spec §2.1–§2.2), Kell's nudges and hint ladder (§11) and the station
// refusals under Tethys retaliation (§9).
// =============================================================================================

const refusalRowSchema = z.strictObject({
  /** The station owners the row applies to. */
  factions: z.array(factionIdSchema).min(1),
  /** M4 template ids the row applies to (`templates.json`); empty = every station of those factions. */
  templates: z.array(id).default(() => []),
  actions: z.array(z.enum(STATION_ACTIONS)).min(1),
  /** The player-facing refusal (no reason given: the operator never says why). */
  message: id,
})
export type RefusalRowConfig = z.output<typeof refusalRowSchema>

export const hooksFileSchema = z.strictObject({
  comment: z.string(),
  firstHour: z.strictObject({
    /** The fixed `evacuation` log the first core salvaged at the Harrow carries (spec §2.2). */
    coreLog: dataId,
    /** The fixed `settlement` fragment of the first First Rush ruin visited (spec §2.2). */
    ruinLog: dataId,
    /** Log sources a generic data core's log is drawn from, by where the core was found. */
    coreSources: z.array(z.enum(LOG_SOURCES)).min(1),
  }),
  harrowHook: z.strictObject({
    /** M2 station ids whose ore receipt is "the Refinery receipt" (spec §2.2): the Bulletin item posts with it. */
    receiptStations: z.array(id).min(1),
    /** "The end of hour one" in game seconds: an ore sale elsewhere posts the item from then on. */
    hourOneS: z.number().positive(),
    headline: id,
    source: wireOutletSchema,
    severity: z.number().int().min(0).max(5),
  }),
  nudges: z.strictObject({
    /** Online days at a tier before Kell's one nudge for it. */
    afterOnlineDays: count,
    /** Online days between the nudge and the first hint, and between hints. */
    hintEveryOnlineDays: z.number().int().positive(),
    /** The highest tier that has a nudge (`kell.nudge.<tier>`); none at the last tier. */
    maxTier: z.number().int().min(1).max(4),
  }),
  refusals: z.partialRecord(z.enum(RETALIATION_ROWS), refusalRowSchema),
})
export type HooksConfig = z.output<typeof hooksFileSchema>

// =============================================================================================
// claims.json (S7): spec §2.3.
// =============================================================================================

const claimSlotSchema = z.strictObject({
  /** Same-type candidates (≥ 2 per slot, S7 tests it): `duration`, `hull`, `verb`, `name`, ... */
  type: id,
  answer: id,
})

export const claimTemplateSchema = z.strictObject({
  tier: claimTier,
  /** The framed recipient (spec §2.3, §3.1); the comms fallback is resolved by S9's `contactFor`. */
  recipient: speakerSchema,
  /** The sentence with `{0}`, `{1}`, ... for the slots. */
  sentence: id,
  slots: z.array(claimSlotSchema).min(3).max(4),
  /** Decoys and same-type distractors added to the pool (pool 8–12 words incl. 2–3 decoys). */
  decoys: z.array(z.strictObject({ type: id, word: id })),
  /** S7: the thread whose proofs Kell's safety net counts ("all of a thread's proofs read", spec §2.3). */
  thread: threadSchema,
  /**
   * S7: the comms fallback when the recipient's dock is refused (spec §3.1): the first entry whose `flag` is set (or
   * that has none) names the speaker; empty = the recipient itself, on comms.
   */
  fallback: z.array(z.strictObject({ flag: storyFlagSchema.optional(), speaker: speakerSchema })).default(() => []),
  /** S7: the open questions this claim answers (spec §2.6); one opens once any `after` log is read. */
  questions: z.array(z.strictObject({ id: dataId, text: z.string().min(1), after: z.array(dataId).min(1) })).default(() => []),
})

export const claimsFileSchema = z.strictObject({
  comment: z.string(),
  /** At most this many attempts per faction day per tier (spec §2.3). */
  attemptsPerDay: z.number().int().positive(),
  unlock: z.strictObject({
    /** Tier 2: proof weight ≥ `proofWeight` in ≥ `threads` threads. */
    tier2: z.strictObject({ proofWeight: nonNegative, threads: z.number().int().positive() }),
    /** Tier 3: `watch` weight ≥ this. */
    tier3: z.strictObject({ watchWeight: nonNegative }),
    /** Tier 4: any of these items held (report core, or its lab-notes fallback). */
    tier4: z.strictObject({ items: z.array(storyItemIdSchema).min(1) }),
    /** Tier 5: any of these items held or flags set (the 50-A file, or Hale's copy terms). */
    tier5: z.strictObject({ items: z.array(storyItemIdSchema), flags: z.array(storyFlagSchema) }),
  }),
  safetyNet: z.strictObject({
    /** Failed attempts before the net opens (with `onlineDaysAfter` passed), or every proof of the thread read. */
    failedAttempts: z.number().int().positive(),
    onlineDaysAfter: day,
    /** Credits only (rev 2). */
    kellFee: nonNegative,
    archivistFee: nonNegative,
    /** S7: Holdfast standing at or below which Kell won't talk, so the Mutual archivist runs the net (spec §2.3). */
    holdfastRefusesAt: standingDelta.default(-50),
  }),
  templates: z.array(claimTemplateSchema),
  /** S7: word id → display text and slot type (ids can't hold `/` or spaces: `ops_r7` reads "OPS-R/7"). */
  words: z.record(dataId, z.strictObject({ text: z.string().min(1), type: id })).default(() => ({})),
  /** S7: the only feedback a wrong claim gets (spec §2.3: never a count, never a per-word hint). */
  feedback: z.strictObject({ wrong: z.string().min(1) }).default(() => ({ wrong: "This doesn't hold up." })),
})
export type ClaimsConfig = z.output<typeof claimsFileSchema>

// =============================================================================================
// case.json (S8): spec §2.4–§2.5.
// =============================================================================================

export const caseFileSchema = z.strictObject({
  comment: z.string(),
  linkCap: z.number().positive(),
  /** `evidenceWeight = meanScale × mean(L) + testimony − credibility`, clamped to 0..1. */
  meanScale: share,
  weights: z.strictObject({ original: nonNegative, log: nonNegative, copyMult: share, texture: nonNegative }),
  testimony: z.strictObject({ okaforPublic: share, okaforAnonymous: share, duarte: share }),
  credibility: z.strictObject({ stipendKnown: share, sponsorGuardian: share, cap: share }),
  /** Case-line thresholds: `circumstantial` from, `documented` from, `damning` from (below = rumour). */
  caseLine: z.strictObject({ circumstantial: share, documented: share, damning: share }),
  bundleMax: z.number().int().positive(),
  meter: z.strictObject({ windowDays: z.number().int().positive(), maxInCase: count }),
  corroboration: z.strictObject({
    weight: nonNegative,
    everyOnlineDays: z.number().int().positive(),
    maxPerLink: count,
    /** Credits per link (the archivist's pull, Duarte's certified extract); 0 = no fee. */
    fees: z.strictObject({ order: nonNegative, knowledge: nonNegative, coverup: nonNegative, complicity: nonNegative }),
    /**
     * S8: the repeatable template log per link (`repeatable: true` in `evidence/*.json`). The `knowledge` one is the
     * meter reading: its instances are `career.story.meterProofs`, the others' are `career.story.corroborations`.
     */
    templates: z
      .strictObject({ order: dataId, knowledge: dataId, coverup: dataId, complicity: dataId })
      .default(() => ({
        order: 'evacuation.corr_insurance_claim',
        knowledge: 'watch.corr_meter_reading',
        coverup: 'report.corr_transfer_order',
        complicity: 'settlement.corr_50a_extract',
      })),
  }),
})
export type CaseConfig = z.output<typeof caseFileSchema>

// =============================================================================================
// clock.json (S10): spec §10.2. The worker caps the result at `gate.json storyMultCap`; `cap` mirrors it.
// =============================================================================================

export const clockFileSchema = z.strictObject({
  comment: z.string(),
  /** The tiers 0–2 row (or the idleRamp). */
  baseMult: mult,
  tierRows: z
    .array(z.strictObject({ tier: z.number().int().min(3).max(5), mult, drop: z.number().positive().max(100), cause: z.string() }))
    .length(3),
  alertStep: z.number().nonnegative().max(1),
  idleRamp: z.strictObject({
    startOnlineDay: day,
    everyOnlineDays: z.number().int().positive(),
    step: z.number().positive().max(1),
    cap: mult,
  }),
  finaleRows: z.strictObject({
    exposePassed: mult,
    exposeFailed: mult,
    blackmail: mult,
    guardiansFloor: mult,
    guardians: mult,
    sell: mult,
    /** S10: an Expose whose vote has not resolved yet (spec §10.2 leaves it open; "Tethys strips while it can"). */
    exposePending: mult,
  }),
  blackmailSurcharge: z.strictObject({ add: z.number().nonnegative().max(2), onlineDays: day }),
  cap: mult,
  /** A tier drop never takes stability below this (and never closes the gate). */
  dropFloor: z.number().min(0).max(100),
  /** S10: the Wire item posted on the online day the idleRamp takes a step (spec §10.2 "Tethys adds fracture shifts"). */
  idleRampWire: z.strictObject({ headline: z.string().min(1), source: wireOutletSchema, severity: z.number().int().min(0).max(3) }),
})
export type ClockConfig = z.output<typeof clockFileSchema>

// =============================================================================================
// leverage.json (S5): spec §9 and Hale's deal (§7).
// =============================================================================================

export const leverageFileSchema = z.strictObject({
  comment: z.string(),
  offers: z.strictObject({
    /** min(principalShare × remaining principal, payments × lease payment); never clears the lease. */
    leased: z.strictObject({ principalShare: share, payments: z.number().positive() }),
    owned: z.strictObject({ cashCap: nonNegative }),
    corporation: z.strictObject({ cashCap: nonNegative }),
  }),
  retaliation: z.strictObject({
    /** Payment cycles before a called debt goes to collections (N ≥ 2). */
    dueCycles: z.number().int().min(2),
    /** Online days of the Asset Recovery hunt (leased stage, refinanced or not). */
    huntOnlineDays: day,
    /** Interdiction chance under the combat row. */
    interdictionChance: share,
    /** A Blackmail exit multiplies the path's retaliation (spec §8.3). */
    blackmailExitMult: mult,
  }),
  refinance: z.strictObject({
    minMutualStanding: standingDelta,
    premiumPerCycle: share,
    obligationEveryCycles: z.number().int().positive(),
    missedObligationStanding: standingDelta,
  }),
  hale: z.strictObject({
    /** Charters refunded with the standing gate waived (corporation). */
    charters: count,
    /** The most bounty credits the deal wipes (only bounties older than the offer). */
    bountyCap: nonNegative,
    /** Restored bounties when the deal is voided by an Expose. */
    voidBountyMult: mult,
  }),
})
export type LeverageConfig = z.output<typeof leverageFileSchema>

// =============================================================================================
// finale.json (S21, S25): spec §8.
// =============================================================================================

/** S25: a Wire post in data (a headline, its outlet and severity 0–3). */
export const wirePostSchema = z.strictObject({ headline: id, source: wireOutletSchema, severity: z.number().int().min(0).max(3) })
export type WirePostConfig = z.output<typeof wirePostSchema>

export const finaleFileSchema = z.strictObject({
  comment: z.string(),
  expose: z.strictObject({
    /** Tethys → player pressure seed at the commit. */
    tethysPressureSeed: z.number(),
    /** A failed vote reopens after the M5 cooldown, only with a new link or corroboration. */
    reopenCooldownDays: day,
    /** Tethys's `tetherstoneRepeal` push after a passed ban. */
    repealAfterOnlineDays: day,
    /** S21: the vote's sponsor after Guardians → Expose when there is no player corporation (Kell's leak, §8.3). */
    fallbackSponsor: factionIdSchema,
  }),
  blackmail: z.strictObject({
    /** The stipend's share of Tethys's AI-only tetherstone income (10 days), fixed at commit. */
    stipendShare: share,
    /** Payment cycles for the clawback lawsuit debt (N ≥ 2). */
    clawbackCycles: z.number().int().min(2),
    /** Online days until the Watch learns and the Candle goes dark. */
    candleDarkOnlineDays: day,
    /** S25: the stipend pays base / stipendIncomeDays × share per online day (the base covers this many days). */
    stipendIncomeDays: z.number().int().positive().default(10),
    /** S25: the stability factor is 1 at or above this stability ... */
    stipendFullAtStability: z.number().min(0).max(100).default(70),
    /** S25: ... and 0 at or below this one (and with the gate closed: no exports, spec §11). */
    stipendZeroAtStability: z.number().min(0).max(100).default(10),
    /** S25: the Wire item when the Candle goes dark (another outlet notes the silence). */
    candleDark: wirePostSchema,
  }),
  guardians: z.strictObject({
    aggressionFloor: z.number().min(0).max(100),
    floorOnlineDays: day,
    /** The floor after Blackmail → Guardians (rev 3). */
    floorAfterBlackmailOnlineDays: day,
    /** S21: the Authority bounty seeded on joining (`wanted`; `law.json wanted` level 2 or more). */
    wantedBounty: nonNegative,
    /** S21: Tethys → player pressure seed when this path lands Tethys's retaliation. */
    tethysPressureSeed: z.number(),
    /** S21: the Tethys security response in vein systems: a Tethys → guardian pressure seed. */
    tethysSecurityPressure: z.number(),
    /** S21: the cell's treasury through `hiddenTransfers` (M5 C11: the Holdfast funds the cell). */
    hiddenTransfer: z.strictObject({ from: factionIdSchema, amount: nonNegative }),
  }),
  sell: z.strictObject({
    meridianTethysPressure: z.number(),
    grievanceFloor: z.number(),
    grievanceOnlineDays: day,
    payout: nonNegative,
    /** "Damaged goods" after Blackmail → Sell (rev 3). */
    payoutAfterBlackmailMult: share,
    /** S21: Tethys → player pressure seed after Blackmail → Sell (Voss blames the player; a plain Sell has none). */
    tethysPressureSeed: z.number(),
  }),
  /** A floor or grievance due offline is extended until the player has been online this many days (§8.2). */
  floorExtensionOnlineDays: day,
  /** The one-way ladder (§8.3): every allowed move; anything else is a typed refusal (T27). */
  ladder: z.array(z.strictObject({ from: finalePathSchema, to: finalePathSchema })),
  epilogue: z.strictObject({
    days: z.number().int().positive(),
    minItems: z.number().int().positive(),
    maxItems: z.number().int().positive(),
    /** S25: words the epilogue never uses (never celebratory, spec §1 rule 9). Matched whole-word, any case. */
    bannedWords: z.array(z.string().min(1)),
    /** S25: the lines per item kind and variant (`epilogue.ts EPILOGUE_KINDS`); tokens {for} {against}. */
    items: z.strictObject({
      path: z.strictObject({ expose: wirePostSchema, blackmail: wirePostSchema, guardians: wirePostSchema, sell: wirePostSchema }),
      broken: z.strictObject({ expose: wirePostSchema, blackmail: wirePostSchema, guardians: wirePostSchema, sell: wirePostSchema }),
      stability: z.strictObject({ normal: wirePostSchema, flickers: wirePostSchema, tremors: wirePostSchema, closed: wirePostSchema }),
      vote: z.strictObject({ pending: wirePostSchema, passed: wirePostSchema, failed: wirePostSchema }),
      okafor: z.strictObject({ aboard: wirePostSchema, safe: wirePostSchema, held: wirePostSchema, absent: wirePostSchema }),
      reyes: z.strictObject({ ally: wirePostSchema, cool: wirePostSchema, hostile: wirePostSchema, dead: wirePostSchema }),
      hale: z.strictObject({ voided: wirePostSchema, deal: wirePostSchema, refused: wirePostSchema }),
      claims: z.strictObject({ corp: wirePostSchema, pilot: wirePostSchema }),
    }),
  }),
  /** S25: the roll call (spec §8.1). Kell's trust = Σ flag weights + perHoldfastContract × contracts; high ≥ highAt, low ≤ lowAt. */
  rollCall: z.strictObject({
    kellTrust: z.strictObject({
      weights: z.partialRecord(storyFlagSchema, z.number()),
      perHoldfastContract: z.number(),
      highAt: z.number(),
      lowAt: z.number(),
    }),
  }),
  /** S25: the "on the payroll" Wire note (spec §8). */
  payroll: wirePostSchema,
  /** S25: the post-finale repeat offers (spec §8.4), each on the existing verbs. */
  repeatables: z.array(
    z.strictObject({
      id: dataId,
      paths: z.array(finalePathSchema).min(1),
      giver: factionIdSchema,
      /** The contract type it runs on (absent = passive, the stipend). */
      contractType: z.enum(CONTRACT_TYPES).optional(),
      everyOnlineDays: z.number().int().positive(),
      /** The first one targets this mission-owned ship (the Guardians' first raid: a TCF lessee barge). */
      firstStoryShip: z.enum(STORY_SHIP_ROLES).optional(),
      needsFlags: z.array(storyFlagSchema).default(() => []),
      /** Only while Tethys's retaliation is in force (the repo hunt). */
      needsRetaliation: z.boolean().default(false),
    }),
  ),
  /** S25: the player corporation's default ballot stance per finale path (spec §8.2): the tetherstone ban and its repeal. */
  corpBallot: z.record(finalePathSchema, z.strictObject({ ban: z.enum(BALLOT_STANCES), repeal: z.enum(BALLOT_STANCES) })),
})
export type FinaleConfig = z.output<typeof finaleFileSchema>

// =============================================================================================
// callbacks.json (S11): spec §1 rule 7, §16.6. `flag` is a `STORY_FLAG` or a `STORY_TRIGGER` (checked on load).
// =============================================================================================

const delayDaysSchema = z
  .union([day, z.tuple([day, day])])
  .transform((d): [number, number] => (typeof d === 'number' ? [d, d] : d))
  .refine(([lo, hi]) => lo <= hi, 'delayDays: min > max')

export const callbackSchema = z.strictObject({
  id: dataId,
  kind: z.enum(['echo', 'callback']),
  flag: id,
  /** Online days; a number or `[min, max]` (seeded pick). */
  delayDays: delayDaysSchema,
  condition: id.optional(),
  channel: z.enum(['wire', 'comms', 'log']),
  source: wireOutletSchema.optional(),
  speaker: speakerSchema.optional(),
  text: z.string(),
  /** S11: the Wire item's severity (1..3, as `wire.json`); Wire callbacks set it (checked by `callbackProblems`). */
  severity: z.number().int().min(1).max(3).optional(),
})
export type StoryCallback = z.output<typeof callbackSchema>

export const callbacksFileSchema = z.strictObject({ comment: z.string(), callbacks: z.array(callbackSchema) })

// =============================================================================================
// reactivation.json (S15; the sim tasks S20, S23, S24 read it): spec §16.8.
// =============================================================================================

const regionShareSchema = z.strictObject({ gate: share, mid: share, deep: share, rim: share })
const regionCreditsSchema = z.strictObject({ gate: nonNegative, mid: nonNegative, deep: nonNegative, rim: nonNegative })

/**
 * The eligibility exclusions (spec §16.2; S15 `eligibility.ts`). The Harrow and the Column Nine field are canon and
 * must stay listed; the others can be switched off for a test by removing them from the list.
 */
export const REACTIVATION_EXCLUSIONS = [
  'harrow',
  'column_nine',
  'okafor_lab_before_m3',
  'guardian_base',
  'hidden_bases',
  'gate_cluster',
] as const
export type ReactivationExclusion = (typeof REACTIVATION_EXCLUSIONS)[number]

const frClassSchema = z.strictObject({
  /** M4 template id (`templates.json`). */
  template: id,
  /** Salvaged components the restart kit needs (spec §16.3 step 3). */
  components: z.partialRecord(z.enum(COMPONENT_TYPES), z.number().int().positive()),
  /** Goods the kit needs (`fuel_cells`, `electronics`: `GoodId`s from the goods block, economy plan M7-1). */
  goods: z.partialRecord(goodIdSchema, z.number().int().positive()),
})

/** A hand-over recipient's finder's fee and standings (spec §16.5); the marked-site and Holdfast deltas are optional. */
const handoverSchema = z.strictObject({
  fee: nonNegative,
  standing: standingDelta,
  markedStanding: standingDelta.optional(),
  holdfastStanding: standingDelta.optional(),
  markedHoldfastStanding: standingDelta.optional(),
})

export const reactivationFileSchema = z.strictObject({
  comment: z.string(),
  eligibility: z.strictObject({
    restorableShare: regionShareSchema,
    classShares: z.strictObject({ habRing: share, oreProcessor: share, depot: share, lab: share }),
    exclusions: z
      .array(z.enum(REACTIVATION_EXCLUSIONS))
      .refine((xs) => xs.includes('harrow') && xs.includes('column_nine'), {
        message: 'the Harrow and the Column Nine field are never restoration targets (spec §16.2)',
      }),
  }),
  /** The survey card (spec §16.3 step 1–2; S15 `survey.ts`). Seeded per POI; placeholders. */
  survey: z.strictObject({
    /** The targeted scan needs this scanner tier (Mk II+). */
    minScannerTier: z.number().int().min(1),
    /** `FR-###` designations are drawn from [min, max]; the Harrow's number is skipped. */
    designation: z.strictObject({ min: z.number().int().positive(), max: z.number().int().positive() }),
    /** Share of restorable stations whose reactor section has a coolant anchor to vent. */
    coolantHazardShare: share,
    /** Share of unmarked restorable stations with a Wrecker squat, per region. */
    squatterShare: regionShareSchema,
    /** The fence fee that pays a Wrecker squat off, per region. */
    fenceFee: regionCreditsSchema,
  }),
  /** The Harrow (FR-217, the Candle): never a restoration site (spec §16.2). Text is a placeholder. */
  harrow: z.strictObject({
    designation: z.number().int().positive(),
    surveyText: z.string().min(1),
    refusal: z.string().min(1),
  }),
  classes: z.strictObject({ habRing: frClassSchema, oreProcessor: frClassSchema, depot: frClassSchema, lab: frClassSchema }),
  /** M8 MC-h4b (U14): a Rush-pattern component in a kit credits fuel cells; a bonus only, never required. */
  rushBonus: z.strictObject({ fuelCellsPerPart: count }),
  refitMult: share,
  refitDaysMult: share,
  registrationFee: nonNegative,
  influence: z.number().nonnegative(),
  upkeep: z.strictObject({ fuelCellsPerDay: count }),
  darkAfterDays: day,
  revertDays: day,
  rerestartMult: share,
  rebootRetryOnlineDays: day,
  title: z.strictObject({ lapseDays: day, fuelCells: count, max: count }),
  handover: z.strictObject({
    mutual: handoverSchema,
    holdfast: handoverSchema,
    authority: handoverSchema,
    meridian: handoverSchema,
    tethys: handoverSchema,
  }),
  holdfast: z.strictObject({
    markerShare: z.strictObject({ gate: share, mid: share, deep: share, rim: share, enclave: share }),
    holdfastShare: share,
    selfRestartStanding: standingDelta,
    familyStanding: standingDelta,
    familyMinStanding: standingDelta,
    /** Family names the survey card can name (placeholders until the naming pass). */
    families: z.array(z.string().min(1)).min(1),
  }),
  tethysChallenge: z.strictObject({
    chance: share,
    fromTier: tierNumber,
    veinHops: count,
    reviewOnlineDays: day,
    fee: nonNegative,
    authorityStanding: standingDelta,
  }),
  authority: z.strictObject({ standingPerRegistration: standingDelta, pettyFeeMult: mult, pettyWaitOnlineDays: day }),
  bunker: z.strictObject({ minT: nonNegative, maxT: nonNegative, veinHops: count, fenceHoldfastStanding: standingDelta }),
  /** S24 (spec §16.9 rule 1): the Tethys fracture site a vein FR station handed to Tethys adds (AI tonnes per faction day). */
  veinRig: z.strictObject({ tonnesPerDay: nonNegative }),
  candleRun: z.strictObject({ holdfastStanding: standingDelta, credits: nonNegative }),
  soak: z.strictObject({ flipRateMax: mult, roiDays: z.tuple([day, day]) }),
})
export type ReactivationConfig = z.output<typeof reactivationFileSchema>

// =============================================================================================
// missions/*.json (S6 runtime; S12, S13 content): plan §2 step machines.
// =============================================================================================

export const missionOutcomeSchema = z.strictObject({
  setFlags: z.array(storyFlagSchema).default(() => []),
  clearFlags: z.array(storyFlagSchema).default(() => []),
  /** S6: `where` names who has the original when it leaves the player (a buyer, `hale`). */
  items: z
    .array(z.strictObject({ id: storyItemIdSchema, state: z.enum(ITEM_STATES), contraband: z.boolean().optional(), where: id.optional() }))
    .default(() => []),
  standings: z.array(z.strictObject({ faction: simFactionIdSchema, delta: standingDelta })).default(() => []),
  /** Fixed credits (formula payouts such as the M1 bounty come from `leverage.ts`, named by `payout`). */
  credits: z.number().default(0),
  payout: id.optional(),
  alerts: z.array(z.enum(CLOCK_ALERTS)).default(() => []),
  reyesState: z.enum(REYES_STATES).optional(),
  okaforState: z.enum(OKAFOR_STATES).optional(),
  commands: z.array(storyCommandSchema).default(() => []),
  /** Comms node to open, Wire echo id, lasting-trace id. */
  comms: id.optional(),
  echo: id.optional(),
})
export type MissionOutcome = z.output<typeof missionOutcomeSchema>

export const missionStepSchema = z.strictObject({
  id: dataId,
  verb: z.enum(STEP_VERBS),
  /** What the step waits on: a site, station, anchor, item or comms node id (S30 resolves sites). */
  target: z.strictObject({ kind: id, ref: id }),
  /** For a contract-type verb: the contract type it reuses (created with no deadline, never in `contracts.active`). */
  contractType: z.enum(CONTRACT_TYPES).optional(),
  item: storyItemIdSchema.optional(),
  /** The §3.1 fallback contact or target when the primary is refused. */
  fallback: z.strictObject({ kind: id, ref: id }).optional(),
  onDone: missionOutcomeSchema.optional(),
})

export const missionForkSchema = z.strictObject({
  id: dataId,
  /** The step after which the fork is offered. */
  afterStep: dataId,
  choices: z
    .array(
      z.strictObject({
        id: dataId,
        /** Comms or UI text key for the cost, shown before commit (spec §1 rule 3). */
        cost: id,
        outcome: missionOutcomeSchema,
      }),
    )
    .min(1),
})

export const missionFileSchema = z.strictObject({
  comment: z.string(),
  id: missionIdSchema,
  offer: z.strictObject({
    tier: z.number().int().min(1).max(5),
    flagsAll: z.array(storyFlagSchema).default(() => []),
    flagsNone: z.array(storyFlagSchema).default(() => []),
    standing: z.array(z.strictObject({ faction: simFactionIdSchema, min: standingDelta })).default(() => []),
    threadWeight: z.array(z.strictObject({ thread: threadSchema, min: nonNegative })).default(() => []),
    contact: speakerSchema,
    /** S6: the comms node the contact opens with the offer (absent = a plain offer notice). */
    comms: id.optional(),
  }),
  steps: z.array(missionStepSchema),
  forks: z.array(missionForkSchema).default(() => []),
  /** Lasting world trace id (spec §1 rule 7) and the echo id (`callbacks.json`). */
  trace: id.optional(),
  echo: id.optional(),
})
export type MissionDef = z.output<typeof missionFileSchema>

// =============================================================================================
// comms/*.json (S9 engine; S12, S13, S25 content): spec §2.7, §3.1.
// =============================================================================================

export const commsReplySchema = z.strictObject({
  id: dataId,
  text: id,
  /** A "Show [log]" reply: shown only when the player holds one of these logs (or a log of this thread/link). */
  show: z
    .strictObject({ logs: z.array(id).default(() => []), thread: threadSchema.optional(), link: caseLinkSchema.optional() })
    .optional(),
  /** The next node id, or absent = the conversation ends. */
  next: id.optional(),
})

export const commsNodeSchema = z.strictObject({
  id: dataId,
  speaker: speakerSchema,
  /** Placeholder portrait id (code-drawn, plan D-TBD); absent = the speaker's default. */
  portrait: id.optional(),
  lines: z.array(id).min(1).max(3),
  /**
   * D-425 career-stage variants of a line about the player: `lines` is the leased text; `owned` (the lease paid
   * off) and `corporation` (fleet notes too) replace it at those stages. Read when the line is delivered.
   */
  variants: z
    .strictObject({ owned: z.array(id).min(1).max(3).optional(), corporation: z.array(id).min(1).max(3).optional() })
    .optional(),
  replies: z.array(commsReplySchema).min(1).max(4),
})
export type CommsNode = z.output<typeof commsNodeSchema>

/**
 * S9: a speaker's display data (`comms/common.json speakers`). Names are placeholders (spec §15 item 7) and live
 * only here, so the naming pass is a data edit. `portrait` is the code-drawn placeholder's id (S31 seeds a
 * silhouette, initials and the faction tint from it).
 */
export const commsSpeakerSchema = z.strictObject({
  name: z.string().min(1),
  faction: factionIdSchema.optional(),
  portrait: id,
})
export type CommsSpeaker = z.output<typeof commsSpeakerSchema>

/** S9: how a contact is reached (spec §3.1). `dock` and `ship` are in person; `comms` works from anywhere. */
export const CONTACT_CHANNELS = ['dock', 'comms', 'ship'] as const
export type ContactChannel = (typeof CONTACT_CHANNELS)[number]

/**
 * S9: one way to reach a §3.1 contact. A `dock` option is refused at hostile standing with `faction` (the owner),
 * at wanted with the owner or `jurisdiction` (unless `lawless`), under the `dockingRefused` retaliation row (owner =
 * the retaliating faction), or below `minStanding`. A `comms` option with a `faction` is refused only when that
 * faction is hostile (it won't talk). A `ship` option is never refused (a ship in space, never a dock).
 */
export const contactOptionSchema = z.strictObject({
  id: dataId,
  speaker: speakerSchema,
  channel: z.enum(CONTACT_CHANNELS),
  /** dock: the station owner (absent = unowned, no gate); comms: the speaker's faction. */
  faction: factionIdSchema.optional(),
  /** dock: the system's jurisdiction when it isn't the owner's (wanted with it refuses too). */
  jurisdiction: factionIdSchema.optional(),
  /** dock: an outlaw station (no wanted check; hostile still refuses, as `docking.ts`). */
  lawless: z.boolean().default(false),
  /** Needs at least this standing with `faction` (the legal archive route, Authority 30). */
  minStanding: standingDelta.optional(),
  /** Ruled out while any of these flags is set (`duarte_exposed`). */
  flagsNone: z.array(storyFlagSchema).default(() => []),
  /** Only while Okafor is in one of these states (absent = any). */
  okaforIn: z.array(z.enum(OKAFOR_STATES)).optional(),
  /** Ruled out while Reyes is in one of these states. */
  reyesNotIn: z.array(z.enum(REYES_STATES)).default(() => []),
  /** The comms node the contact opens with, when it speaks first. */
  node: id.optional(),
})
export type ContactOption = z.output<typeof contactOptionSchema>

/** S9: the §3.1 table (`comms/common.json contacts`): the primary option first, then the fallbacks in order. */
export const contactsSchema = z.strictObject({
  /** Docking is refused at standing ≤ this (M5 §12.5 hostile). */
  hostileStanding: standingDelta,
  /** Docking is refused at wanted ≥ this. */
  wantedLevel: z.number().int().positive(),
  /** The faction whose `dockingRefused` retaliation row closes its docks (spec §9). */
  retaliationFaction: factionIdSchema,
  /** The generic refusal node per reason (played before the fallback). */
  refusalNodes: z.strictObject({ hostile: id, wanted: id, retaliation: id, standing: id }),
  interactions: z.record(z.enum(CONTACT_INTERACTIONS), z.array(contactOptionSchema).min(1)),
})
export type ContactsConfig = z.output<typeof contactsSchema>

/** `speakers` and `contacts` live in `common.json` only (S9 checks they are there; other comms files omit them). */
export const commsFileSchema = z.strictObject({
  comment: z.string(),
  nodes: z.array(commsNodeSchema),
  speakers: z.partialRecord(speakerSchema, commsSpeakerSchema).optional(),
  contacts: contactsSchema.optional(),
})
